import React from 'react';
import { motion } from 'framer-motion';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import useFetch from './useFetch.jsx';
import { domain } from './Domain.jsx';
import { 
    Globe, Users, Award, Shield, Heart, Star, 
    TrendingUp, MapPin, Phone, Mail, Clock,
    CheckCircle, ArrowRight, Play, Target,
    Zap, Users2, Calendar, DollarSign, Loader2
} from 'lucide-react';

const AboutUs = () => {
    // Fetch statistics from backend
    const { data: statsData, loading: statsLoading } = useFetch(`${domain}/statistics`);

    // Dynamic stats from backend with fallback values
    const stats = [
        { 
            icon: <Users className="w-6 h-6" />, 
            value: statsData?.total_users ? (statsData.total_users >= 1000 ? `${(statsData.total_users / 1000).toFixed(1)}K+` : statsData.total_users.toString()) : "50K+", 
            label: "Happy Users",
            loading: statsLoading
        },
        { 
            icon: <Calendar className="w-6 h-6" />, 
            value: statsData?.total_events ? (statsData.total_events >= 1000 ? `${(statsData.total_events / 1000).toFixed(1)}K+` : statsData.total_events.toString()) : "10K+", 
            label: "Events Created",
            loading: statsLoading
        },
        { 
            icon: <Star className="w-6 h-6" />, 
            value: statsData?.average_rating ? `${statsData.average_rating}★` : "4.8★", 
            label: "Average Rating",
            loading: statsLoading
        },
        { 
            icon: <TrendingUp className="w-6 h-6" />, 
            value: statsData?.uptime_percentage ? `${statsData.uptime_percentage}%` : "99%", 
            label: "Platform Uptime",
            loading: statsLoading
        }
    ];

    const values = [
        {
            icon: <Heart className="w-8 h-8" />,
            title: "User-Centric",
            description: "We put our users first, ensuring every feature and decision enhances their event experience."
        },
        {
            icon: <Shield className="w-8 h-8" />,
            title: "Trust & Security",
            description: "Your data and transactions are protected with enterprise-grade security measures."
        },
        {
            icon: <Zap className="w-8 h-8" />,
            title: "Innovation",
            description: "Constantly evolving and improving to provide cutting-edge event management solutions."
        },
        {
            icon: <Users2 className="w-8 h-8" />,
            title: "Community",
            description: "Building a vibrant community of event organizers and attendees across Uganda."
        }
    ];

    const features = [
        "Easy event creation and management",
        "Secure payment processing",
        "Real-time analytics and insights",
        "Mobile-responsive design",
        "24/7 customer support",
        "Multi-language support",
        "Advanced search and filtering",
        "Social media integration"
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            
            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-orange-50 via-white to-teal-50 py-20">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
                            <Globe className="w-4 h-4" />
                            About EventPlots
                        </div>
                        <h1 className="text-lg font-bold text-gray-900 mb-6">
                            Connecting People Through
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-teal-500"> Amazing Events</span>
                        </h1>
                        <p className="text-sm text-gray-600 mb-8 leading-relaxed">
                            EventPlots is Uganda's premier event management platform, empowering organizers to create 
                            unforgettable experiences and helping attendees discover the best events in their area.
                        </p>
                        {statsLoading && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex items-center justify-center gap-2 mb-6"
                            >
                                <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                                <span className="text-sm text-gray-600">Loading statistics...</span>
                            </motion.div>
                        )}
                        <div className="flex flex-wrap justify-center gap-8">
                            {stats.map((stat, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="text-center"
                                >
                                    {stat.loading ? (
                                        <>
                                            <div className="flex items-center justify-center w-12 h-12 bg-gray-200 rounded-xl mb-3 mx-auto animate-pulse">
                                                <div className="w-6 h-6 bg-gray-300 rounded"></div>
                                            </div>
                                            <div className="h-6 w-16 bg-gray-200 rounded animate-pulse mx-auto mb-2"></div>
                                            <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mx-auto"></div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl text-white mb-3 mx-auto">
                                                {stat.icon}
                                            </div>
                                            <div className="text-sm font-bold text-gray-900 mb-1">
                                                {stat.value}
                                            </div>
                                            <div className="text-sm text-gray-600">{stat.label}</div>
                                        </>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-4">
                                <Target className="w-4 h-4" />
                                Our Mission
                            </div>
                            <h2 className="text-lg font-bold text-gray-900 mb-6">
                                Revolutionizing Event Management in Uganda
                            </h2>
                            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                                We're on a mission to make event creation and discovery seamless, accessible, and enjoyable 
                                for everyone. From small community gatherings to large-scale corporate events, EventPlots 
                                provides the tools and platform needed to bring people together.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-gray-700">Empower event organizers with powerful tools</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-gray-700">Connect communities through meaningful events</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-gray-700">Drive economic growth in the events industry</p>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="bg-gradient-to-br from-orange-300 via-orange-200 to-teal-300 rounded-3xl p-10 shadow-xl relative overflow-hidden">
                                {/* Decorative circles */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-teal-200 rounded-full opacity-30 -translate-y-8 translate-x-8"></div>
                                <div className="absolute bottom-0 right-0 w-24 h-24 bg-teal-300 rounded-full opacity-40 translate-y-4 translate-x-4"></div>
                                
                                <div className="relative z-10">
                                    <h3 className="text-xl font-bold mb-5 text-gray-900">Our Vision</h3>
                                    <p className="text-base text-gray-800 mb-8 leading-relaxed">
                                        To become the leading event platform in East Africa, fostering a vibrant ecosystem 
                                        where creativity thrives and communities flourish through shared experiences.
                                    </p>
                                    <div className="flex items-center gap-2 text-gray-900 hover:text-orange-600 transition-colors cursor-pointer group">
                                        <TrendingUp className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                        <span className="font-semibold">Growing together</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-lg font-bold text-gray-900 mb-4">Our Core Values</h2>
                        <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                            These principles guide everything we do and shape the culture of our organization.
                        </p>
                    </motion.div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                            >
                                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl text-white mb-4 mx-auto">
                                    {value.icon}
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">{value.title}</h3>
                                <p className="text-sm text-gray-600 text-center leading-relaxed">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-20 bg-gradient-to-br from-orange-50 to-teal-50">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-lg font-bold text-gray-900 mb-6">
                                Why Choose EventPlots?
                            </h2>
                            <p className="text-sm text-gray-600 mb-8 leading-relaxed">
                                We've built EventPlots with the needs of both event organizers and attendees in mind. 
                                Our platform offers comprehensive features designed to make event management effortless.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {features.map((feature, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                        <span className="text-gray-700">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="bg-white rounded-2xl p-8 shadow-xl">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                                        <Play className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Platform Demo</h3>
                                        <p className="text-sm text-gray-600">See EventPlots in action</p>
                                    </div>
                                </div>
                                <div className="bg-gray-100 rounded-lg p-4 mb-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Experience the difference</span>
                                        <ArrowRight className="w-4 h-4 text-orange-500" />
                                    </div>
                                </div>
                                <button className="w-full bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg">
                                    Get Started Today
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AboutUs; 